import type { MedplumClient } from "@medplum/core";
import { AgentChat, createAgentChat } from "@repo/agent-chat";
import { SidebarInset, SidebarProvider } from "@repo/ui/components/ui/sidebar";
import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, Outlet, redirect, useLocation } from "@tanstack/react-router";
import { MessageSquare } from "lucide-react";
import { type AppRoute, AppSidebar } from "@/components/app-sidebar";
import { API_BASE_URL } from "@/lib/api-client";
import { medplum } from "@/lib/medplum";

interface MyRouterContext {
  queryClient: QueryClient;
  medplum: MedplumClient;
}

const routes: AppRoute[] = [
  {
    title: "Chat",
    url: "/chat",
    icon: MessageSquare,
  },
];

export const Route = createRootRouteWithContext<MyRouterContext>()({
  beforeLoad: ({ context, location }) => {
    // Skip auth check for login page
    if (location.pathname === "/login") {
      return;
    }

    const profile = context.medplum.getProfile();
    if (!profile) {
      throw redirect({
        to: "/login",
        search: { redirect: location.pathname },
      });
    }
  },
  component: RootComponent,
});

const chatClient = createAgentChat({ auth: medplum, baseUrl: API_BASE_URL });

function RootComponent() {
  const location = useLocation();

  // Login page renders without sidebar
  if (location.pathname === "/login") {
    return <Outlet />;
  }

  return (
    <AgentChat.Provider client={chatClient}>
      <SidebarProvider defaultOpen={false} className="h-svh">
        <AppSidebar routes={routes} />
        <SidebarInset className="overflow-hidden flex flex-col">
          <Outlet />
        </SidebarInset>
      </SidebarProvider>
    </AgentChat.Provider>
  );
}
